import React from 'react';
import { Mail20Regular } from '@fluentui/react-icons';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLocalizedStrings } from 'i18n';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';

import { INotificationSettingsSummaryProps, NotificationSettingsSummary } from '../NotificationSettingsSummary';

interface IEmailNotificationSettingsSummaryProps
  extends Omit<
    INotificationSettingsSummaryProps,
    'notificationType' | 'toggleNotificationLabel' | 'notificationsEnabled' | 'onToggleNotification' | 'telemetryProps'
  > {}

export const EmailNotificationSettingsSummary = (props: IEmailNotificationSettingsSummaryProps) => {
  const { onDrillDown } = props;
  const { notificationSettingsSummary } = useNotificationSettingsSummary();
  const { updateSelfNotificationSettings } = useNotificationsDispatch();
  const { t } = useLocalizedStrings();
  const { userPrincipalName } = pageDataService.getUserData();

  const onToggle = React.useCallback(
    (isEnabled: boolean) => {
      updateSelfNotificationSettings({ isEnabled, channel: 'Email' });
    },
    [updateSelfNotificationSettings]
  );

  const text = t('OfficeShellPanel.NotificationSettings.Sections.Email.Text', {
    emailAddress: userPrincipalName,
  });
  const confirmTitle = t('OfficeShellPanel.NotificationSettings.Sections.Email.TurnOffConfirm.Title', {
    emailAddress: userPrincipalName,
  });
  const confirmSubtext = t('OfficeShellPanel.NotificationSettings.Sections.Email.TurnOffConfirm.Subtext', {
    emailAddress: userPrincipalName,
  });

  const notificationsEnabled = notificationSettingsSummary?.emailNotificationSetting.isEnabled ?? false;
  const toggleNotificationLabel = notificationsEnabled
    ? t('OfficeShellPanel.NotificationSettings.Sections.On')
    : t('OfficeShellPanel.NotificationSettings.Sections.Off');

  const enabledTopicShortNames = React.useMemo(() => {
    if (onDrillDown) {
      if (notificationSettingsSummary?.emailNotificationSetting.areAllTopicsEnabled) {
        return [t('OfficeShellPanel.NotificationSettings.Sections.AllNotifications')];
      }
      return notificationSettingsSummary?.emailNotificationSetting.enabledTopicShortNames;
    }
    return undefined;
  }, [
    notificationSettingsSummary?.emailNotificationSetting.areAllTopicsEnabled,
    notificationSettingsSummary?.emailNotificationSetting.enabledTopicShortNames,
    onDrillDown,
    t,
  ]);

  return (
    <NotificationSettingsSummary
      notificationType="Email"
      title={t('OfficeShellPanel.NotificationSettings.Sections.Email.Title')}
      iconComponent={Mail20Regular}
      text={text}
      toggleNotificationLabel={toggleNotificationLabel}
      toggleAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Email.Title')}
      notificationsEnabled={notificationsEnabled}
      isToggleable={notificationSettingsSummary?.emailNotificationSetting.isToggleable}
      onToggleNotification={onToggle}
      turnOffConfirmStrings={{
        title: confirmTitle,
        subText: confirmSubtext,
      }}
      enabledTopicShortNames={enabledTopicShortNames}
      {...props}
    />
  );
};
